import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectGuaranteesSupervisorSegmentsByGroup,
  selectGuaranteesSupervisorSegmentsByGroupError,
  selectGuaranteesSupervisorSegmentsByGroupStatus,
} from '~/store/guarantees/selectors.ts';
import {
  createGuaranteesSupervisorSegmentSupervisor,
  deleteGuaranteesSupervisorSegmentSupervisor,
  fetchGuaranteesSupervisorSegmentsByGroup,
} from '~/store/guarantees/thunks/supervisorSegmentThunks.ts';
import { GuaranteesFetchStatus, type TGuaranteesSupervisorGroupKey } from '~/store/guarantees/types.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import type { RootState } from '~/store/store.ts';
import { mapGuaranteesSupervisorSegmentsToAdministrationSections } from './utils.ts';

type TUseGuaranteesSupervisorSegmentsGroupParams = {
  groupKey: TGuaranteesSupervisorGroupKey;
  enabled: boolean;
};

export const useGuaranteesSupervisorSegmentsGroup = ({
  groupKey,
  enabled,
}: TUseGuaranteesSupervisorSegmentsGroupParams) => {
  const dispatch = useAppDispatch();
  const [searchEmail, setSearchEmail] = useState('');
  const [addingSectionId, setAddingSectionId] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const segments = useSelector((state: RootState) =>
    selectGuaranteesSupervisorSegmentsByGroup(state, groupKey)
  );
  const status = useSelector((state: RootState) =>
    selectGuaranteesSupervisorSegmentsByGroupStatus(state, groupKey)
  );
  const error = useSelector((state: RootState) =>
    selectGuaranteesSupervisorSegmentsByGroupError(state, groupKey)
  );
  const sections = useMemo(
    () => mapGuaranteesSupervisorSegmentsToAdministrationSections(segments),
    [segments]
  );

  useEffect(() => {
    if (!enabled || status !== GuaranteesFetchStatus.Idle) {
      return;
    }

    void dispatch(fetchGuaranteesSupervisorSegmentsByGroup({ groupKey }));
  }, [dispatch, enabled, groupKey, status]);

  const handleSearchSubmit = async (email: string) => {
    const nextSearchEmail = email.trim();

    setSearchEmail(nextSearchEmail);
    await dispatch(
      fetchGuaranteesSupervisorSegmentsByGroup({
        groupKey,
        email: nextSearchEmail,
      })
    ).unwrap();
  };

  const handleAddUser = async (segmentId: string, supervisorEmail: string) => {
    setAddingSectionId(segmentId);

    try {
      await dispatch(
        createGuaranteesSupervisorSegmentSupervisor({
          segmentId,
          supervisorEmail,
          groupKey,
          email: searchEmail,
        })
      ).unwrap();
    } finally {
      setAddingSectionId(null);
    }
  };

  const handleDeleteUser = async (segmentId: string, supervisorId: string) => {
    setDeletingUserId(supervisorId);

    try {
      await dispatch(
        deleteGuaranteesSupervisorSegmentSupervisor({
          segmentId,
          supervisorId,
          groupKey,
          email: searchEmail,
        })
      ).unwrap();
    } finally {
      setDeletingUserId(null);
    }
  };

  return {
    addingSectionId,
    deletingUserId,
    errorMessage: error?.message ?? '',
    handleAddUser,
    handleDeleteUser,
    handleSearchSubmit,
    isLoading: status === GuaranteesFetchStatus.Loading,
    searchEmail,
    sections,
    status,
  };
};
